import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import FormModalActions from "../../components/FormModalActions";
import {
  STANDARD_FORM_DIALOG_PROPS,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../utils/formModalStyles";
import { createIpdPrescription, getIpdPrescriptionsByPatientId } from "../../services/ipdPrescriptionService";
import { getAllMedicine, createNewMedicine } from "../../services/medicineNewService";
import { getAllLabTests, createLabTestInBaseTable } from "../../services/labtestService";
import { getComplainList } from "../../services/complaintService";
import { getDiagnosisList } from "../../services/diagnosisService";
import { getDoctorById, getDoctorByUserId } from "../../services/doctorService";
import { getAllStaff } from "../../services/StaffService";
import { createLabTest } from "../../services/PatientlabTestService";
import { getPatientById } from "../../services/patientService";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import { getOrganizationName } from "../../utils/organizationUtils";
import {
  buildPrescriptionDocumentHtml,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";

const dosageOptions = [
  "1-0-0",
  "1-0-1",
  "1-1-0",
  "1-1-1",
  "0-0-1",
  "0-1-0",
  "0-1-1",
  "0-0-0",
];
const frequencyOptions = ["daily", "weekly", "monthly"];
const treatmentTypes = ["Medicine", "IV Fluids"];

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  const nested =
    data?.message ||
    data?.error ||
    data?.detail ||
    (Array.isArray(data?.errors) ? data.errors[0]?.defaultMessage || data.errors[0]?.message : null);
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  if (error?.response?.status === 400) {
    return "The server rejected this prescription (400). A field was invalid or incomplete — refill and submit again.";
  }
  return error?.message || fallback;
};

const buildVitalSignsPayload = (vitalSigns = {}) => {
  const payload = {};
  Object.entries(vitalSigns || {}).forEach(([key, value]) => {
    if (value === "" || value == null) return;
    if (key === "bpReading") {
      payload[key] = value;
      return;
    }
    const n = Number(value);
    payload[key] = Number.isFinite(n) ? n : value;
  });
  return Object.keys(payload).length > 0 ? [payload] : [];
};

const IpdPrescriptionModal = ({ 
  open, 
  onClose, 
  patientId, 
  patientName, 
  admissionId,
  admission = null,
  doctorId 
}) => {
  const { user } = useAuth();
  const [medicines, setMedicines] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [diagnosis, setDiagnosis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [patient_appointment, setPatientAppointment] = useState(null);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [pathDeskStaff, setPathDeskStaff] = useState([]);
  const [showAdditionalComplaints, setShowAdditionalComplaints] = useState(false);
  const [showAdditionalDiagnoses, setShowAdditionalDiagnoses] = useState(false);
  const [showManualMedicineInput, setShowManualMedicineInput] = useState({}); // Object with medicine index as key
  const [showAdditionalLabTests, setShowAdditionalLabTests] = useState(false);
  // State for patient name to display in heading
  const [displayPatientName, setDisplayPatientName] = useState("");
  // Fetch patient details if name is missing
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (!patientId) {
        setDisplayPatientName("");
        return;
      }
      
      // Set initial value from prop
      if (patientName && patientName.trim() !== "") {
        setDisplayPatientName(patientName.trim());
      } else {
        setDisplayPatientName("");
      }
      
      // If patient name is missing but we have patientId, fetch patient details from organization-specific table
      if ((!patientName || patientName.trim() === "") && patientId) {
        try {
          console.log(`[IpdPrescriptionModal] Fetching patient details for patientId: ${patientId}`);
          const patientData = await getPatientById(patientId);
          console.log(`[IpdPrescriptionModal] Fetched patient data:`, patientData);
          
          if (patientData) {
            // Try multiple possible property names for patient name
            let fetchedName = patientData.name || 
                             patientData.patientName || 
                             "";
            
            // If name is still empty, try combining firstName and lastName
            if (!fetchedName || fetchedName.trim() === "") {
              const firstName = patientData.firstName || "";
              const lastName = patientData.lastName || "";
              const combined = `${firstName} ${lastName}`.trim();
              if (combined) {
                fetchedName = combined;
              }
            }
            
            if (fetchedName && fetchedName.trim() !== "") {
              setDisplayPatientName(fetchedName.trim());
              console.log(`[IpdPrescriptionModal] ✅ Set displayPatientName to: ${fetchedName.trim()}`);
            }
          }
        } catch (error) {
          console.error(`[IpdPrescriptionModal] Error fetching patient details:`, error);
          // Keep empty if fetch fails
        }
      }
    };
    
    if (open) {
      fetchPatientDetails();
    }
  }, [open, patientId, patientName]);

  useEffect(() => {
    if (open) {
      Promise.all([
        fetchComplaints(),
        fetchDiagnosis(),
        fetchLabTests(),
        fetchMedicines(),
        fetchPatientAppointment(),
        fetchDoctorInfo(),
        fetchStaffList(),
        fetchPathDeskStaff(),
      ]);
    }
  }, [open]);

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      // Fetch medicines from base raw medicine table (not organization-specific)
      const data = await getAllMedicine();
      // Transform the data to match the expected format (medicineName -> name, id -> medicineId)
      const transformedData = Array.isArray(data) ? data.map(medicine => ({
        id: medicine.id,
        medicineId: medicine.id,
        name: medicine.medicineName || medicine.name || '',
        medicineName: medicine.medicineName || medicine.name || '',
        medicineType: medicine.medicineType || '',
        manufacturer: medicine.manufacturer || '',
        price: medicine.price || 0,
        costPerUnit: medicine.costPerUnit || 0,
      })) : [];
      setMedicines(transformedData);
    } catch (error) {
      console.error("Error fetching medicines:", error);
      setMedicines([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchComplaints = async () => {
    try {
      const data = await getComplainList();
      setComplaints(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchDiagnosis = async () => {
    try {
      const data = await getDiagnosisList();
      setDiagnosis(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLabTests = async () => {
    try {
      // Fetch lab tests from base lab test table (not organization-specific)
      const data = await getAllLabTests();
      setLabTests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching lab tests:", error);
      setLabTests([]); // Set empty array on error
    }
  };

  const fetchPatientAppointment = async () => {
    setFetching(true);
    try {
      const data = await getIpdPrescriptionsByPatientId(patientId);
      if (data && data.length > 0) {
        setPatientAppointment(data[data.length - 1]);
      } else {
        setPatientAppointment(null);
      }
    } catch (error) {
      console.log("No previous IPD prescriptions found or API not implemented yet:", error);
      setPatientAppointment(null);
    }
    setFetching(false);
  };

  const fetchDoctorInfo = async () => {
    try {
      if (doctorId) {
        // Try to get doctor by doctor ID first
        try {
          const doctor = await getDoctorById(doctorId);
          setDoctorInfo(doctor);
          return;
        } catch (doctorError) {
          console.log("Doctor not found by ID, trying by user ID:", doctorError);
        }
      }
      
      // If doctorId failed or not provided, try to get doctor by user ID
      if (user?.id) {
        try {
          const doctor = await getDoctorByUserId(user.id);
          setDoctorInfo(doctor);
          return;
        } catch (userError) {
          console.log("Doctor not found by user ID:", userError);
        }
      }
      
      // Fallback to current user info if no doctor found
      setDoctorInfo({
        name: user?.name || user?.doctorName || "Doctor",
        id: user?.id
      });
    } catch (error) {
      console.error("Error fetching doctor info:", error);
      // Fallback to current user info if doctor not found
      setDoctorInfo({
        name: user?.name || user?.doctorName || "Doctor",
        id: user?.id
      });
    }
  };

  const fetchStaffList = async () => {
    try {
      const staff = await getAllStaff();
      setStaffList(staff || []);
    } catch (error) {
      console.error("Error fetching staff list:", error);
      setStaffList([]);
    }
  };

  const fetchPathDeskStaff = async () => {
    try {
      const staff = await getAllStaff();
      const pathDeskUsers = staff.filter((s) => s.userRole === "pathdesk");
      setPathDeskStaff(pathDeskUsers || []);
    } catch (error) {
      console.error("Error fetching path desk staff:", error);
      setPathDeskStaff([]);
    }
  };

  const validationSchema = Yup.object({
    complaints: Yup.array().test(
      "at-least-one-complaint",
      "At least one complaint is required (either select from list or enter manually)",
      function(value) {
        const { additionalComplaints } = this.parent;
        const hasSelectedComplaints = value && value.length > 0;
        const hasManualComplaints = additionalComplaints && additionalComplaints.trim().length > 0;
        return hasSelectedComplaints || hasManualComplaints;
      }
    ),
    diagnoses: Yup.array().test(
      "at-least-one-diagnosis",
      "At least one diagnosis is required (either select from list or enter manually)",
      function(value) {
        const { additionalDiagnoses } = this.parent;
        const hasSelectedDiagnoses = value && value.length > 0;
        const hasManualDiagnoses = additionalDiagnoses && additionalDiagnoses.trim().length > 0;
        return hasSelectedDiagnoses || hasManualDiagnoses;
      }
    ),
    additionalComplaints: Yup.string().optional(),
    additionalDiagnoses: Yup.string().optional(),
    medicines: Yup.array().of(
      Yup.object({
        name: Yup.string().required("Medicine name is required"),
        dosage: Yup.string().required("Dosage is required"),
        frequency: Yup.string().required("Frequency is required"),
        duration: Yup.string()
          .required("Duration is required")
          .test("is-number", "Duration must be a number", function(value) {
            if (!value || value.trim() === "") return false;
            return !isNaN(Number(value)) && Number(value) > 0;
          }),
        notes: Yup.string().optional("Medicine notes is optional"),
      })
    ),
    medicineNotes: Yup.string().optional("Medicine notes is optional"),
    vitalSigns: Yup.object({
      bpReading: Yup.string().test(
        "is-bp-or-empty",
        "BP Reading must be a number or systolic/diastolic (e.g. 120/80)",
        function (value) {
          if (!value || value.trim() === "") return true;
          return /^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/.test(value.trim());
        }
      ),
      weightReading: Yup.string().test("is-number-or-empty", "Weight Reading must be a number", function(value) {
        if (!value || value.trim() === "") return true; // Allow empty
        return !isNaN(Number(value)) && Number(value) >= 0;
      }),
      heightReading: Yup.string().test("is-number-or-empty", "Height Reading must be a number", function(value) {
        if (!value || value.trim() === "") return true; // Allow empty
        return !isNaN(Number(value)) && Number(value) >= 0;
      }),
      oxygenSaturation: Yup.string().test("is-number-or-empty", "Oxygen Saturation must be a number", function(value) {
        if (!value || value.trim() === "") return true; // Allow empty
        return !isNaN(Number(value)) && Number(value) >= 0;
      }),
      pulseRate: Yup.string().test("is-number-or-empty", "Pulse Rate must be a number", function(value) {
        if (!value || value.trim() === "") return true; // Allow empty
        return !isNaN(Number(value)) && Number(value) >= 0;
      }),
      respirationRate: Yup.string().test("is-number-or-empty", "Respiration Rate must be a number", function(value) {
        if (!value || value.trim() === "") return true; // Allow empty
        return !isNaN(Number(value)) && Number(value) >= 0;
      }),
    }),
    patientLabTests: Yup.array(),
  });

  const initialValues = {
    patientId: patientId,
    doctorId: doctorId,
    appointmentId: null, // IPD doesn't have appointment ID
    medicines: [
      {
        medicineId: null,
        name: "",
        dosage: "",
        frequency: "",
        duration: "",
        notes: "",
        treatmentType: "Medicine",
        performedBy: "",
      },
    ],
    medicineNotes: "",
    nextVisitDate: "",
    isExtraMedicine: false,
    numberOfDays: 0,
    complaints: [],
    diagnoses: [],
    additionalComplaints: "",
    additionalDiagnoses: "",
    vitalSigns: {
      bpReading: "",
      weightReading: "",
      heightReading: "",
      oxygenSaturation: "",
      pulseRate: "",
      respirationRate: "",
    },
    patientLabTests: [],
    additionalLabTests: "",
    assignedLabId: "",
  };

  const handleSubmit = async (values) => {
    // Ensure all manual medicines are saved and have IDs before processing
    const medicinesWithIds = [];
    for (let i = 0; i < values.medicines.length; i++) {
      const medicine = values.medicines[i];
      if (medicine.name && medicine.name.trim()) {
        let medicineId = medicine.medicineId;
        
        // If medicine doesn't have an ID, save it to base table
        if (!medicineId) {
          console.log(`[IpdPrescriptionModal] Saving manual medicine "${medicine.name}" before submission...`);
          
          // Check if medicine already exists
          const existingMedicine = medicines.find(
            (med) => (med.name || med.medicineName || '').toLowerCase().trim() === medicine.name.toLowerCase().trim()
          );
          
          if (existingMedicine) {
            medicineId = existingMedicine.id || existingMedicine.medicineId;
          } else {
            // Create new medicine in base table
            try {
              const medicineData = {
                medicineName: medicine.name.trim(),
                medicineType: "Homeopathy",
                manufacturer: "Unknown",
                supplier: "Unknown",
                details: 1,
                price: 0,
                costPerUnit: 0,
                medicineDetails: "tablet",
                userId: user?.id || 1,
              };
              
              const createdMedicine = await createNewMedicine(medicineData);
              medicineId = createdMedicine.id;
              console.log(`[IpdPrescriptionModal] ✅ Created medicine "${medicine.name}" with ID: ${medicineId}`);
              
              // Refresh medicine list
              await fetchMedicines();
            } catch (error) {
              console.error(`[IpdPrescriptionModal] Error saving medicine "${medicine.name}":`, error);
              alert(`Failed to save medicine "${medicine.name}". Please try again.`);
              return; // Stop submission if medicine save failed
            }
          }
        }
        
        medicinesWithIds.push({
          ...medicine,
          medicineId: medicineId,
        });
      }
    }
    
    // Copy the common notes to each medicine object
    const medicinesWithNotes = medicinesWithIds.map(medicine => ({
      ...medicine,
      notes: values.medicineNotes || "", // Set common notes to each medicine
    }));
    
    // Process additional complaints - split by newline or comma and trim
    const additionalComplaintsArray = values.additionalComplaints
      ? values.additionalComplaints
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Process additional diagnoses - split by newline or comma and trim
    const additionalDiagnosesArray = values.additionalDiagnoses
      ? values.additionalDiagnoses
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Merge selected and additional complaints/diagnoses, removing duplicates
    const allComplaints = [...new Set([...(values.complaints || []), ...additionalComplaintsArray])];
    const allDiagnoses = [...new Set([...(values.diagnoses || []), ...additionalDiagnosesArray])];
    
    // Process additional lab tests - split by newline or comma and trim
    const additionalLabTestsArray = values.additionalLabTests
      ? values.additionalLabTests
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Save manual lab tests to base table and get their IDs
    let allLabTestNames = [...(values.patientLabTests || [])];
    if (additionalLabTestsArray.length > 0) {
      console.log(`[IpdPrescriptionModal] Saving ${additionalLabTestsArray.length} manual lab tests before submission...`);
      const savedLabTests = await saveManualLabTests(additionalLabTestsArray);
      
      // Refresh lab tests list to include newly saved tests
      await fetchLabTests();
      
      // Add saved lab test names to the selected list
      const savedTestNames = savedLabTests.map(test => test.name);
      allLabTestNames = [...new Set([...allLabTestNames, ...savedTestNames])];
    }
    
    // Determine if we should create lab tests separately (when assignedLabId is provided)
    // If creating separately, don't include in prescription payload to avoid duplicates
    const shouldCreateLabTestsSeparately = allLabTestNames && allLabTestNames.length > 0 && values.assignedLabId;
    
    // Prepare lab tests - include all lab tests (both selected and manually entered) with their IDs
    // Refresh labtests array to ensure we have the latest data including newly saved tests
    let selectedLabTests = [];
    if (!shouldCreateLabTestsSeparately && allLabTestNames.length > 0) {
      const currentLabTestsList = await getAllLabTests();
      selectedLabTests = currentLabTestsList
        .filter((test) => allLabTestNames.includes(test.name))
        .map((test) => ({
          labTestId: test.id,
          testName: test.name,
          status: 'PENDING'
        }));
    }
    
    const resolvedAdmissionId = Number(admissionId);
    const admissionIdForPayload =
      Number.isFinite(resolvedAdmissionId) && resolvedAdmissionId > 0
        ? resolvedAdmissionId
        : null;

    // Create IPD prescription data that integrates with existing IPD workflow
    const ipdPrescriptionDataRaw = {
      patientId: values.patientId,
      doctorId: values.doctorId,
      ...(admissionIdForPayload
        ? {
            admissionId: admissionIdForPayload,
            admission_id: admissionIdForPayload,
          }
        : {}),
      prescriptionDate: new Date().toISOString(),
      complaints: allComplaints,
      diagnoses: allDiagnoses,
      vitalSigns: buildVitalSignsPayload(values.vitalSigns),
      nextVisitDate: values.nextVisitDate,
      remarks: values.medicineNotes,
      // Medicines will be converted to individual treatment records
      medicines: medicinesWithNotes.map(medicine => ({
        medicineId: medicine.medicineId,
        medicineName: medicine.name,
        treatmentType: medicine.treatmentType,
        dosage: medicine.dosage,
        frequency: medicine.frequency,
        duration: medicine.duration,
        notes: medicine.notes,
        performedBy: medicine.performedBy,
        status: 'PENDING' // Will be administered by nurses
      })),
      // Only include lab tests in prescription if NOT creating them separately
      // This prevents duplicate creation (backend creates them, then frontend creates them again)
      ...(shouldCreateLabTestsSeparately ? {} : {
        patientLabTests: selectedLabTests
      }),
      numberOfDays: values.numberOfDays,
      isExtraMedicine: values.isExtraMedicine,
      deductInventory: false,
      prescriptionType: "IPD", // Mark as IPD prescription
      // This will create individual treatment records in ipd_treatments table
      createTreatmentRecords: true
    };
    
    // Remove organizationId from prescription object if it exists (apiClient will add it as query param)
    // This ensures the backend receives organizationId as query parameter, not in body
    const { organizationId: orgIdInBody, ...ipdPrescriptionData } = ipdPrescriptionDataRaw;
    
    console.log("Creating IPD prescription with treatment integration:", ipdPrescriptionData);
    console.log("apiClient will automatically add organizationId as query parameter");
    
    try {
      const data = await createIpdPrescription(ipdPrescriptionData, {
        admission: admission || null,
      });
      
      // Extract prescription group ID from the response
      // The response structure is: { success: true, message: "...", data: { id: ..., ... } }
      const prescriptionGroupId = data?.data?.id || data?.id || null;
      
      // Create lab test assignments separately if lab tests are selected and assigned lab ID is provided
      // This ensures lab assignment is properly handled and avoids duplicate creation
      if (shouldCreateLabTestsSeparately) {
        try {
          // Refresh lab tests list to get latest including newly saved ones
          const currentLabTestsList = await getAllLabTests();
          const labTestPayloads = allLabTestNames.map((testName) => {
            const test = currentLabTestsList.find(t => t.name === testName);
            
            if (!test) {
              console.warn(`[IpdPrescriptionModal] Lab test "${testName}" not found in base table`);
              return null;
            }
            
            return {
              patientId: values.patientId,
              admissionId: admissionId,
              testId: test.id,
              labId: values.assignedLabId, // Use selected lab ID
              assignedBy: values.doctorId,
              testDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
              testStatus: "PENDING",
              testResult: "",
              prescriptionGroupId: prescriptionGroupId // Link lab test to the prescription
            };
          }).filter(Boolean); // Remove null entries
          
          if (labTestPayloads.length > 0) {
            await createLabTest(labTestPayloads);
            console.log("Lab tests assigned to selected path desk staff successfully with prescriptionGroupId:", prescriptionGroupId);
          }
        } catch (labError) {
          console.error("Error assigning lab tests:", labError);
          // Don't fail the entire prescription if lab assignment fails
        }
      }
      
      alert("IPD Prescription created successfully! Treatment records have been created for nurse administration.");
      onClose();
    } catch (error) {
      console.error("[IpdPrescriptionModal] Error creating IPD prescription:", error);
      console.error("[IpdPrescriptionModal] Error response:", error.response?.data);
      console.error("[IpdPrescriptionModal] Error status:", error.response?.status);
      
      let errorMessage = getApiErrorMessage(
        error,
        "Failed to create IPD prescription. Please try again."
      );

      if (error.response?.status === 500) {
        const backendMessage = error.response?.data?.message || '';
        
        if (backendMessage.includes('Failed to save IPD prescription group to table')) {
          errorMessage = `Failed to create IPD prescription. The organization-specific table may not exist or there may be a database issue. `;
          errorMessage += `Please contact the administrator to ensure the IPD prescription tables are properly set up for your organization.`;
        } else if (backendMessage.includes('Patient not found')) {
          errorMessage = `Patient not found. The patient with ID ${values.patientId} does not exist in the organization-specific patient table.`;
        } else if (backendMessage.includes('Admission not found')) {
          errorMessage = `Admission not found. The admission with ID ${admissionId} does not exist.`;
        } else if (backendMessage) {
          errorMessage = `Backend error: ${backendMessage}`;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'IPD prescription endpoint not found. Please check backend configuration.';
      }
      
      alert(errorMessage);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const buildPrescriptionPrintHtml = (values) => {
    const additionalComplaintsArray = values.additionalComplaints
      ? values.additionalComplaints
          .split(/\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];
    const additionalDiagnosesArray = values.additionalDiagnoses
      ? values.additionalDiagnoses
          .split(/\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];

    return buildPrescriptionDocumentHtml({
      hospitalName: getOrganizationName(user) || "Hospital",
      patient: {
        id: patientId || "N/A",
        name: displayPatientName || patientName || "Patient",
        mobile: "",
        address: "",
      },
      visitDate: dayjs().format("DD MMM YYYY"),
      doctorName: doctorInfo?.name || doctorInfo?.doctorName || user?.name || user?.doctorName || "Doctor",
      vitals: values.vitalSigns || {},
      complaints: [...new Set([...(values.complaints || []), ...additionalComplaintsArray])],
      diagnoses: [...new Set([...(values.diagnoses || []), ...additionalDiagnosesArray])],
      medicines: (values.medicines || [])
        .filter((med) => med?.name)
        .map((med) => ({
          ...med,
          notes: staffList.find((staff) => staff.id === med.performedBy)?.name || med.notes || "Not assigned",
        })),
      advice: values.medicineNotes ? [values.medicineNotes] : [],
      investigations: values.patientLabTests || [],
      followUp: values.nextVisitDate || "",
    });
  };

  const handlePrint = (values) => {
    printPatientSummaryDocument({
      title: `Rx-${displayPatientName || patientName || "Patient"}-${patientId || "N/A"}`,
      bodyHtml: buildPrescriptionPrintHtml(values),
    });
  };

  const handleSaveAndPrint = async(values, validateForm, submitForm) => {
    // Validate the form first
    const validationErrors = await validateForm();
    
    // Check if there are any validation errors
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      // Check for specific required field errors
      const hasComplaintError = validationErrors.complaints;
      const hasDiagnosisError = validationErrors.diagnoses;
      const hasMedicineErrors = validationErrors.medicines && validationErrors.medicines.length > 0;
      
      let errorMessage = "Please fill in all required fields:\n";
      if (hasComplaintError) errorMessage += "- At least one complaint is required\n";
      if (hasDiagnosisError) errorMessage += "- At least one diagnosis is required\n";
      if (hasMedicineErrors) {
        errorMessage += "- Please fill in all medicine details (name, dosage, frequency, duration) for all medicines\n";
      }
      
      alert(errorMessage);
      return; // Don't proceed with print or submit
    }
    
    // If validation passes, proceed with print and submit
    await new Promise((resolve) => {
      handlePrint(values);
      setTimeout(() => resolve(), 1000); // Wait for 1 second (assuming print finishes within this time)
    });
 
    await new Promise((resolve) => {
      handleSubmit(values); // trigger the submit
      setTimeout(() => resolve(), 1000); // Wait for 1 second (assuming print finishes within this time)
    });
  };

  // Function to get distinct medicines
  const getDistinctMedicines = (medicines) => {
    const seen = new Set();
    return medicines.filter((medicine) => {
      const medicineName = medicine.name || medicine.medicineName || '';
      const duplicate = seen.has(medicineName);
      seen.add(medicineName);
      return !duplicate;
    });
  };

  // Function to save manual medicine entry to base table
  const saveManualMedicine = async (medicineName, setFieldValue, index) => {
    try {
      if (!medicineName || !medicineName.trim()) {
        return null;
      }

      // Check if medicine already exists
      const existingMedicine = medicines.find(
        (med) => (med.name || med.medicineName || '').toLowerCase().trim() === medicineName.toLowerCase().trim()
      );

      if (existingMedicine) {
        // Medicine already exists, use its ID
        setFieldValue(`medicines.${index}.name`, existingMedicine.name || existingMedicine.medicineName);
        setFieldValue(`medicines.${index}.medicineId`, existingMedicine.id || existingMedicine.medicineId);
        return existingMedicine.id || existingMedicine.medicineId;
      }

      // Create new medicine in base table with minimal required fields
      const medicineData = {
        medicineName: medicineName.trim(),
        medicineType: "Homeopathy", // Default type
        manufacturer: "Unknown", // Default manufacturer
        supplier: "Unknown", // Default supplier
        details: 1, // Default details
        price: 0, // Default price
        costPerUnit: 0, // Default cost per unit
        medicineDetails: "tablet", // Default details
        userId: user?.id || 1, // User ID
      };

      console.log(`[IpdPrescriptionModal] Creating medicine "${medicineName}" in base table...`);
      const createdMedicine = await createNewMedicine(medicineData);
      console.log(`[IpdPrescriptionModal] ✅ Created medicine "${medicineName}" with ID: ${createdMedicine.id}`);

      // Update the medicine list and form field
      setFieldValue(`medicines.${index}.name`, createdMedicine.medicineName || medicineName.trim());
      setFieldValue(`medicines.${index}.medicineId`, createdMedicine.id);

      // Refresh medicine list to include the new medicine
      await fetchMedicines();

      return createdMedicine.id;
    } catch (error) {
      console.error(`[IpdPrescriptionModal] Error saving manual medicine "${medicineName}":`, error);
      alert(`Failed to save medicine "${medicineName}". Please try again or select from the list.`);
      return null;
    }
  };

  // Function to save manual lab test entries to base table
  const saveManualLabTests = async (labTestNames) => {
    try {
      if (!labTestNames || labTestNames.length === 0) {
        return [];
      }

      const savedLabTests = [];

      for (const testName of labTestNames) {
        if (!testName || !testName.trim()) continue;

        // Check if lab test already exists
        const existingTest = labTests.find(
          (test) => (test.name || '').toLowerCase().trim() === testName.toLowerCase().trim()
        );

        if (existingTest) {
          savedLabTests.push({ name: existingTest.name, id: existingTest.id });
          continue;
        }

        // Create new lab test in base table with minimal required fields
        const labTestData = {
          name: testName.trim(),
          amount: 0, // Default amount
        };

        console.log(`[IpdPrescriptionModal] Creating lab test "${testName}" in base table...`);
        const createdTest = await createLabTestInBaseTable(labTestData);
        console.log(`[IpdPrescriptionModal] ✅ Created lab test "${testName}" with ID: ${createdTest.id}`);

        savedLabTests.push({ name: createdTest.name || testName.trim(), id: createdTest.id });
      }

      // Refresh lab test list to include new tests
      if (savedLabTests.length > 0) {
        await fetchLabTests();
      }

      return savedLabTests;
    } catch (error) {
      console.error(`[IpdPrescriptionModal] Error saving manual lab tests:`, error);
      alert(`Failed to save some lab tests. Please try again or select from the list.`);
      return [];
    }
  };

  // Function to filter medicines based on treatment type
  const getFilteredMedicines = (type) => {
    if (!type) return [];

    if (type === "Medicine") {
      // For Medicine, show all medicines EXCEPT Fluid
      return medicines.filter((m) => m.medicineType !== "FLuids");
    }

    if (type === "IV Fluids") {
      // For IV Fluids, show only Fluid medicines
      return medicines.filter((m) => m.medicineType === "FLuids");
    }

    return [];
  };
console.log( "medicines list  : " ,medicines);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      {...STANDARD_FORM_DIALOG_PROPS}
      sx={STANDARD_FORM_DIALOG_PAPER_SX}
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ ...STANDARD_FORM_DIALOG_TITLE_SX, position: "relative", pr: 6 }}>
        Patient Id {patientId || "N/A"} {displayPatientName || patientName || "Patient"}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              touched,
              errors,
              isSubmitting,
              validateForm,
              submitForm,
            }) => {
              // Helper: numeric fields; BP also allows systolic/diastolic (e.g. 120/80)
              const handleNumericChange = (e, fieldName) => {
                const value = e.target.value;
                const isBpField = fieldName === "vitalSigns.bpReading";
                const pattern = isBpField
                  ? /^\d*\.?\d*(\/\d*\.?\d*)?$/
                  : /^\d*\.?\d*$/;
                if (value === "" || pattern.test(value)) {
                  setFieldValue(fieldName, value);
                }
              };

              return (
              <>
                <Form>
                  {/* Doctor Name */}
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Doctor Name"
                        name="doctorName"
                        value={doctorInfo?.name || doctorInfo?.doctorName || user?.name || user?.doctorName || "Doctor"}
                        disabled={true}
                      />
                    </Grid>
                    {/* Complaints */}
                    <Grid item xs={12}>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={1}
                        alignItems={"center"}
                      >
                        <Autocomplete
                          multiple
                          fullWidth
                          options={["Add Custom Complaint...", ...complaints.map((ele) => ele.name)]}
                          value={values.complaints}
                          onChange={(event, newValue) => {
                            // Check if "Add Custom Complaint..." was selected
                            if (newValue && newValue.includes("Add Custom Complaint...")) {
                              // Remove "Add Custom Complaint..." from the array
                              const filteredValue = newValue.filter(item => item !== "Add Custom Complaint...");
                              setFieldValue("complaints", filteredValue);
                              // Show the additional complaints text field
                              setShowAdditionalComplaints(true);
                            } else {
                              setFieldValue("complaints", newValue);
                            }
                          }}
                          getOptionDisabled={(option) => option === "Add Custom Complaint..." && showAdditionalComplaints}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Complaints"
                              placeholder="Select Complaints"
                              error={touched.complaints && Boolean(errors.complaints)}
                              helperText={touched.complaints && errors.complaints}
                            />
                          )}
                        />
                        <Tooltip title="Load Previous">
                          <IconButton
                            onClick={() => {
                              setFieldValue(
                                "complaints",
                                patient_appointment?.complaints || []
                              );
                            }}
                            disabled={
                              fetching || !patient_appointment || Object.keys(patient_appointment).length === 0
                            }
                          >
                            <UndoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>

                    {/* Additional Complaints Text Field */}
                    {showAdditionalComplaints && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            // label="Additional Complaints (Enter manually, one per line or separated by commas)"
                            name="additionalComplaints"
                            value={values.additionalComplaints}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter additional complaints manually..."
                            // helperText="You can enter multiple complaints, one per line or separated by commas"
                          />
                          <IconButton
                            onClick={() => {
                              setShowAdditionalComplaints(false);
                              setFieldValue("additionalComplaints", "");
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    )}

                    {/* Diagnoses */}
                    <Grid item xs={12}>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={1}
                        alignItems={"center"}
                      >
                        <Autocomplete
                          fullWidth
                          multiple
                          options={["Add Custom Diagnosis...", ...diagnosis.map((ele) => ele.diagnosisName)]}
                          value={values.diagnoses}
                          onChange={(event, newValue) => {
                            // Check if "Add Custom Diagnosis..." was selected
                            if (newValue && newValue.includes("Add Custom Diagnosis...")) {
                              // Remove "Add Custom Diagnosis..." from the array
                              const filteredValue = newValue.filter(item => item !== "Add Custom Diagnosis...");
                              setFieldValue("diagnoses", filteredValue);
                              // Show the additional diagnoses text field
                              setShowAdditionalDiagnoses(true);
                            } else {
                              setFieldValue("diagnoses", newValue);
                            }
                          }}
                          getOptionDisabled={(option) => option === "Add Custom Diagnosis..." && showAdditionalDiagnoses}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="Diagnoses"
                              placeholder="Select Diagnoses"
                              error={touched.diagnoses && Boolean(errors.diagnoses)}
                              helperText={touched.diagnoses && errors.diagnoses}
                            />
                          )}
                        />
                        <Tooltip title="Load Previous">
                          <IconButton
                            onClick={() => {
                              setFieldValue(
                                "diagnoses",
                                patient_appointment?.diagnoses || []
                              );
                            }}
                            disabled={
                              fetching || !patient_appointment || Object.keys(patient_appointment).length === 0
                            }
                          >
                            <UndoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>

                    {/* Additional Diagnoses Text Field */}
                    {showAdditionalDiagnoses && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            // label="Additional Diagnoses (Enter manually, one per line or separated by commas)"
                            name="additionalDiagnoses"
                            value={values.additionalDiagnoses}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter additional diagnoses manually..."
                            // helperText="You can enter multiple diagnoses, one per line or separated by commas"
                          />
                          <IconButton
                            onClick={() => {
                              setShowAdditionalDiagnoses(false);
                              setFieldValue("additionalDiagnoses", "");
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    )}

                    {/* Vital Signs */}
                    <Grid item xs={12}>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={1}
                        alignItems={"center"}
                      >
                        <Typography variant="h6" fontWeight={"bold"}>
                          Vital Signs
                        </Typography>
                        <Tooltip title="Load Previous">
                          <IconButton
                            onClick={() => {
                              setFieldValue(
                                "vitalSigns.bpReading",
                                patient_appointment?.vitalSigns?.[0]?.bpReading || ""
                              );
                              setFieldValue(
                                "vitalSigns.weightReading",
                                patient_appointment?.vitalSigns?.[0]?.weightReading || ""
                              );
                              setFieldValue(
                                "vitalSigns.heightReading",
                                patient_appointment?.vitalSigns?.[0]?.heightReading || ""
                              );
                              setFieldValue(
                                "vitalSigns.pulseRate",
                                patient_appointment?.vitalSigns?.[0]?.pulseRate || ""
                              );
                              setFieldValue(
                                "vitalSigns.respirationRate",
                                patient_appointment?.vitalSigns?.[0]?.respirationRate || ""
                              );
                              setFieldValue(
                                "vitalSigns.oxygenSaturation",
                                patient_appointment?.vitalSigns?.[0]?.oxygenSaturation || ""
                              );
                            }}
                            disabled={
                              fetching || !patient_appointment || Object.keys(patient_appointment).length === 0
                            }
                          >
                            <UndoOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="BP Reading"
                        name="vitalSigns.bpReading"
                        value={values.vitalSigns.bpReading}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">mmHg</InputAdornment>
                          ),
                        }}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.bpReading")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.bpReading &&
                          Boolean(errors.vitalSigns?.bpReading)
                        }
                        helperText={
                          touched.vitalSigns?.bpReading && errors.vitalSigns?.bpReading
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Weight Reading"
                        name="vitalSigns.weightReading"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">kg</InputAdornment>
                          ),
                        }}
                        value={values.vitalSigns.weightReading}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.weightReading")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.weightReading &&
                          Boolean(errors.vitalSigns?.weightReading)
                        }
                        helperText={
                          touched.vitalSigns?.weightReading &&
                          errors.vitalSigns?.weightReading
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Height Reading"
                        name="vitalSigns.heightReading"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">cm</InputAdornment>
                          ),
                        }}
                        value={values.vitalSigns.heightReading}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.heightReading")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.heightReading &&
                          Boolean(errors.vitalSigns?.heightReading)
                        }
                        helperText={
                          touched.vitalSigns?.heightReading &&
                          errors.vitalSigns?.heightReading
                        }
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Pulse Rate"
                        name="vitalSigns.pulseRate"
                        value={values.vitalSigns.pulseRate}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">bpm</InputAdornment>
                          ),
                        }}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.pulseRate")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.pulseRate &&
                          Boolean(errors.vitalSigns?.pulseRate)
                        }
                        helperText={
                          touched.vitalSigns?.pulseRate && errors.vitalSigns?.pulseRate
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Respiration Rate"
                        name="vitalSigns.respirationRate"
                        value={values.vitalSigns.respirationRate}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.respirationRate")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.respirationRate &&
                          Boolean(errors.vitalSigns?.respirationRate)
                        }
                        helperText={
                          touched.vitalSigns?.respirationRate &&
                          errors.vitalSigns?.respirationRate
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Oxygen Saturation"
                        name="vitalSigns.oxygenSaturation"
                        value={values.vitalSigns.oxygenSaturation}
                        onChange={(e) => handleNumericChange(e, "vitalSigns.oxygenSaturation")}
                        onBlur={handleBlur}
                        error={
                          touched.vitalSigns?.oxygenSaturation &&
                          Boolean(errors.vitalSigns?.oxygenSaturation)
                        }
                        helperText={
                          touched.vitalSigns?.oxygenSaturation &&
                          errors.vitalSigns?.oxygenSaturation
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        gap={1}
                        alignItems={"center"}
                      >
                        <Typography variant="h6" fontWeight={"bold"}>
                          Medicine
                        </Typography>
                        <Tooltip title="Load Previous">
                          <IconButton
                            onClick={() => {
                              const previousMedicines = patient_appointment?.medicines || [];
                              setFieldValue("medicines", previousMedicines);
                              
                              // Extract common notes from the first medicine (if any)
                              const commonNotes = previousMedicines.length > 0 ? previousMedicines[0].notes || "" : "";
                              setFieldValue("medicineNotes", commonNotes);
                            }}
                            disabled={
                              fetching || !patient_appointment || Object.keys(patient_appointment).length === 0
                            }
                          >
                            <UndoOutlinedIcon /> 
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Medicine</TableCell>
                            <TableCell>Dosage</TableCell>
                            <TableCell>Frequency</TableCell>
                            <TableCell sx={{ width: "150px" }}>Duration</TableCell>
                            <TableCell>Performed By</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <FieldArray name="medicines">
                            {({ push, remove }) => (
                              <>
                                {values.medicines.map((medicine, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                      <TextField
                                        select
                                        name={`medicines.${index}.treatmentType`}
                                        value={medicine.treatmentType}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        size="small"
                                        fullWidth
                                      >
                                        {treatmentTypes.map((type) => (
                                          <MenuItem key={type} value={type}>
                                            {type}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </TableCell>
                                    <TableCell>
                                      {showManualMedicineInput[index] ? (
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                          <TextField
                                            fullWidth
                                            variant="outlined"
                                            label="Enter Medicine Name"
                                            name={`medicines.${index}.name`}
                                            value={medicine.name}
                                            onChange={handleChange}
                                            onBlur={async (e) => {
                                              handleBlur(e);
                                              // Save medicine to base table when user finishes typing
                                              if (medicine.name && medicine.name.trim() && !medicine.medicineId) {
                                                await saveManualMedicine(medicine.name, setFieldValue, index);
                                              }
                                            }}
                                            error={
                                              touched.medicines?.[index]?.name &&
                                              Boolean(errors.medicines?.[index]?.name)
                                            }
                                            helperText={
                                              touched.medicines?.[index]?.name &&
                                              errors.medicines?.[index]?.name
                                            }
                                            placeholder="Enter medicine name manually..."
                                          />
                                          <IconButton
                                            onClick={() => {
                                              setShowManualMedicineInput(prev => ({ ...prev, [index]: false }));
                                              setFieldValue(`medicines.${index}.name`, "");
                                              setFieldValue(`medicines.${index}.medicineId`, null);
                                            }}
                                            size="small"
                                            sx={{ mt: 1 }}
                                          >
                                            <CloseIcon />
                                          </IconButton>
                                        </Box>
                                      ) : (
                                        <Autocomplete
                                          options={[
                                            { name: "Create New Medicine...", isCreateOption: true },
                                            ...getDistinctMedicines(getFilteredMedicines(medicine.treatmentType))
                                          ]}
                                          getOptionLabel={(option) => option.name || option.medicineName || ""}
                                          value={
                                            getFilteredMedicines(medicine.treatmentType).find(
                                              (option) => (option.name || option.medicineName) === medicine.name
                                            ) || null
                                          }
                                          onChange={(event, newValue) => {
                                            if (newValue && newValue.isCreateOption) {
                                              // Show manual medicine input field instead of opening modal
                                              setShowManualMedicineInput(prev => ({ ...prev, [index]: true }));
                                            } else {
                                              setFieldValue(
                                                `medicines.${index}.name`,
                                                newValue?.name || newValue?.medicineName || ""
                                              );
                                              setFieldValue(
                                                `medicines.${index}.medicineId`,
                                                newValue?.medicineId || newValue?.id || null
                                              );
                                              // Hide manual input if medicine is selected from list
                                              setShowManualMedicineInput(prev => ({ ...prev, [index]: false }));
                                            }
                                          }}
                                          getOptionDisabled={(option) => option.isCreateOption && showManualMedicineInput[index]}
                                          renderOption={(props, option) => (
                                            <li {...props} key={option.isCreateOption ? "create-new" : option.id || option.name}>
                                              <Box sx={{ 
                                                fontWeight: option.isCreateOption ? 'bold' : 'normal',
                                                color: option.isCreateOption ? 'primary.main' : 'inherit'
                                              }}>
                                                {option.isCreateOption ? "+ " : ""}{option.name || option.medicineName}
                                              </Box>
                                            </li>
                                          )}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              label="Select Medicine"
                                              error={
                                                touched.medicines?.[index]?.name &&
                                                Boolean(errors.medicines?.[index]?.name)
                                              }
                                              helperText={
                                                touched.medicines?.[index]?.name &&
                                                errors.medicines?.[index]?.name
                                              }
                                            />
                                          )}
                                          fullWidth
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        select
                                        name={`medicines.${index}.dosage`}
                                        value={medicine.dosage}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.medicines?.[index]?.dosage &&
                                          Boolean(errors.medicines?.[index]?.dosage)
                                        }
                                        helperText={
                                          touched.medicines?.[index]?.dosage &&
                                          errors.medicines?.[index]?.dosage
                                        }
                                        fullWidth
                                      >
                                        {dosageOptions.map((option, idx) => (
                                          <MenuItem key={idx} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        select
                                        name={`medicines.${index}.frequency`}
                                        value={medicine.frequency}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                          touched.medicines?.[index]?.frequency &&
                                          Boolean(errors.medicines?.[index]?.frequency)
                                        }
                                        helperText={
                                          touched.medicines?.[index]?.frequency &&
                                          errors.medicines?.[index]?.frequency
                                        }
                                        fullWidth
                                      >
                                        {frequencyOptions.map((option, idx) => (
                                          <MenuItem key={idx} value={option}>
                                            {option}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        name={`medicines.${index}.duration`}
                                        value={medicine.duration}
                                        onChange={(e) => handleNumericChange(e, `medicines.${index}.duration`)}
                                        onBlur={handleBlur}
                                        InputProps={{
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              days
                                            </InputAdornment>
                                          ),
                                        }}
                                        error={
                                          touched.medicines?.[index]?.duration &&
                                          Boolean(errors.medicines?.[index]?.duration)
                                        }
                                        helperText={
                                          touched.medicines?.[index]?.duration &&
                                          errors.medicines?.[index]?.duration
                                        }
                                        fullWidth
                                        placeholder="Duration"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <TextField
                                        select
                                        name={`medicines.${index}.performedBy`}
                                        value={medicine.performedBy}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        size="small"
                                        fullWidth
                                        error={
                                          touched.medicines?.[index]?.performedBy &&
                                          Boolean(errors.medicines?.[index]?.performedBy)
                                        }
                                        helperText={
                                          touched.medicines?.[index]?.performedBy &&
                                          errors.medicines?.[index]?.performedBy
                                        }
                                      >
                                        {staffList
                                          .filter((staff) => staff.userRole === "staff")
                                          .map((staff) => (
                                            <MenuItem key={staff.id} value={staff.id}>
                                              {staff.name}
                                            </MenuItem>
                                          ))}
                                      </TextField>
                                    </TableCell>
                                    <TableCell>
                                      <IconButton
                                        onClick={() => remove(index)}
                                        aria-label="delete"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow>
                                  <TableCell colSpan={8} sx={{ textAlign: "right" }}>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={() =>
                                        push({
                                          name: "",
                                          dosage: "",
                                          frequency: "",
                                          duration: "",
                                          notes: "",
                                          treatmentType: "Medicine",
                                          performedBy: "",
                                        })
                                      }
                                    >
                                      Add Medicine
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}
                          </FieldArray>
                        </TableBody>
                      </Table>
                    </Grid>

                    {/* Common Medicine Notes */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Medicine Notes"
                        name="medicineNotes"
                        value={values.medicineNotes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.medicineNotes && Boolean(errors.medicineNotes)}
                        placeholder="Enter common notes for all medicines..."
                      />
                    </Grid>

                    {/* Lab Test, Next Visit Date, and Assignment in single row */}
                    <Grid item xs={4}>
                      <Autocomplete
                        multiple
                        options={["Create New Lab Test...", ...labTests.map((ele) => ele.name)]}
                        value={values.patientLabTests}
                        onChange={(event, newValue) => {
                          // Check if "Create New Lab Test..." was selected
                          if (newValue && newValue.includes("Create New Lab Test...")) {
                            // Remove "Create New Lab Test..." from the array
                            const filteredValue = newValue.filter(item => item !== "Create New Lab Test...");
                            setFieldValue("patientLabTests", filteredValue);
                            // Show manual lab test input field instead of opening modal
                            setShowAdditionalLabTests(true);
                          } else {
                            setFieldValue("patientLabTests", newValue);
                          }
                        }}
                        getOptionDisabled={(option) => option === "Create New Lab Test..." && showAdditionalLabTests}
                        renderOption={(props, option) => (
                          <li {...props} key={option}>
                            <Box sx={{ 
                              fontWeight: option === "Create New Lab Test..." ? 'bold' : 'normal',
                              color: option === "Create New Lab Test..." ? 'primary.main' : 'inherit'
                            }}>
                              {option === "Create New Lab Test..." ? "+ " : ""}{option}
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Select Lab Test"
                            placeholder="Select Lab Test"
                            error={
                              touched.patientLabTests && Boolean(errors.patientLabTests)
                            }
                            helperText={
                              touched.patientLabTests && errors.patientLabTests
                            }
                          />
                        )}
                      />
                    </Grid>

                    {/* Additional Lab Tests Text Field */}
                    {showAdditionalLabTests && (
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            name="additionalLabTests"
                            value={values.additionalLabTests || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter additional lab tests manually..."
                          />
                          <IconButton
                            onClick={() => {
                              setShowAdditionalLabTests(false);
                              setFieldValue("additionalLabTests", "");
                            }}
                            size="small"
                            sx={{ mt: 1 }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Next Visit Date"
                        name="nextVisitDate"
                        value={values.nextVisitDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.nextVisitDate && Boolean(errors.nextVisitDate)}
                        helperText={touched.nextVisitDate && errors.nextVisitDate}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: new Date().toISOString().split("T")[0],
                        }}
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        select
                        label="Assign Lab Tests To"
                        name="assignedLabId"
                        value={values.assignedLabId || ""}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.assignedLabId && Boolean(errors.assignedLabId)}
                        helperText={touched.assignedLabId && errors.assignedLabId}
                      >
                        {pathDeskStaff.length === 0 ? (
                          <MenuItem disabled value="">
                            No path desk staff available
                          </MenuItem>
                        ) : (
                          pathDeskStaff.map((staff) => (
                            <MenuItem key={staff.id} value={staff.userId}>
                              {staff.name}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
                        <FormModalActions
                          onCancel={handleClose}
                          submitType="submit"
                          loading={isSubmitting}
                          sx={{ width: "auto", px: 0 }}
                        />
                        <Button
                          size="large"
                          variant="contained"
                          color="primary"
                          onClick={async () => {
                            await handleSaveAndPrint(values, validateForm, submitForm);
                          }}
                          disabled={isSubmitting}
                          sx={FORM_SUBMIT_BUTTON_SX}
                        >
                          Submit and Print
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Form>
              
               </>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IpdPrescriptionModal;
